import {
  LocaleProvider,
  CofsClientProvider,
  BXPExpressClientProvider,
  CnasClientProvider,
  HamletClientProvider,
  IdentityClientProvider,
  StargateClientProvider,
} from "@atlassiansox/bxpkit-core";

import { SurfaceProvider } from "./contexts";
import Environment from "../common/environment";
import {
  CofsClient,
  PUBLIC_PROD_BASE_URL as PROD_BASE_URL_COFS,
  STAGING_BASE_URL as STAGING_BASE_URL_COFS,
} from "@atlassiansox/asc-cofs";
import { StargateClient } from "@atlassiansox/asc-stargate";
import { IdentityClient } from "@atlassiansox/asc-identity";
import {
  BXPExpressClient,
  DEFAULT_BASE_URL as STAGING_BASE_URL_BXP_EXPRESS,
} from "@atlassiansox/bxp-express-client";
import { FeatureFlagsProvider } from "@atlassiansox/bxp-react-feature-flags";
import {
  CnasClient,
  PRODUCTION_BASE_URL as PRODUCTION_BASE_URL_CNAS,
  STAGING_BASE_URL as STAGING_BASE_URL_CNAS,
} from "@atlassiansox/asc-cnas";
import {
  HamletClient,
  DEFAULT_BASE_URL as DEFAULT_BASE_URL_HAMS,
} from "@atlassiansox/asc-hamlet";
import { setBooleanFeatureFlagResolver } from "@atlaskit/platform-feature-flags";
import React from "react";
import ReactDOM from "react-dom";
import { addLocaleData, IntlProvider } from "react-intl";
import { IntlProvider as IntlNextProvider } from "react-intl-next";

import * as componentList from "./component-list";
import { Suspense } from "react";
import WacAppSwitcher from "./app-switcher/app";

import { FetchPricingProvider } from "@atlassiansox/multi-product-calculator";
import { fetchPricing } from "../utils/get-catalog-client";

import UFOSegment from "@atlaskit/react-ufo/segment";

// set Available Components
const availableComponents = {
  ...componentList,
  WacAppSwitcher,
};

class App {
  init() {
    this.findComponents();
    this.registerPlatformFeatureFlags();
  }

  findComponents() {
    let renderedComponentTypes = [];
    const componentTags = document.querySelectorAll(
      'script[type="text/jsx-component"]'
    );

    componentTags.forEach((element) => {
      const params = this.parseComponentProps(element);

      if (params) {
        if (params.renderOnce && !!renderedComponentTypes[params.type]) {
          return;
        }
        renderedComponentTypes[params.type] = true;

        const defaultTrackProperties = {
          eventType: "Track",
          eventContainer: null,
          eventColumn: null,
          magnoliaComponentId: params.domRootId || null,
          eventComponent: params.type || null,
          event: "clicked",
          inputValue: null,
          parentLabel: null,
          label: null,
          productKey: null,
          productVersion: null,
          productEdition: null,
          productPlatform: null,
          schemaVersion: 1,
        };
        this.renderComponent({ defaultTrackProperties, ...params });
      }
    });
  }

  parseComponentProps(element) {
    const componentData = element.innerHTML
      .replace("/* <sl:translate_json> */", "")
      .replace("/* </sl:translate_json> */", "");

    try {
      return JSON.parse(componentData);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  registerPlatformFeatureFlags() {
    try {
      /**
       * Platform Feature flags (PFF): https://developer.atlassian.com/cloud/framework/atlassian-frontend/development/feature-flagging/platform-feature-flags/
       *
       * This creates a PFF resolver that allows calls to `getBooleanFF` inside of Platform components to use your feature flag client
       * and LD project, rather than installing a feature flag client of their own, or passing those flags in via props.
       */
      setBooleanFeatureFlagResolver((flagKey) =>
        Boolean(window?.featureFlags?.[flagKey])
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Could not initialize Platform Feature Flags", error);
    }
  }

  getClients() {
    const isDev = Environment.isDev();
    const isStaging = Environment.isStaging();
    const cnasBaseUrl = isStaging
      ? STAGING_BASE_URL_CNAS
      : PRODUCTION_BASE_URL_CNAS;
    const bxpExpressBaseUrl = isStaging
      ? STAGING_BASE_URL_BXP_EXPRESS
      : `${Environment.getWacIntegrationUrl()}/endpoint`;
    const cofsBaseUrl =
      isDev || isStaging ? STAGING_BASE_URL_COFS : PROD_BASE_URL_COFS;
    const hamsBaseUrl = isStaging
      ? `${Environment.getWacIntegrationUrl()}/apis/stg/hamlet`
      : DEFAULT_BASE_URL_HAMS;
    const cofsClient = new CofsClient({ baseUrl: cofsBaseUrl });
    const cnasClient = new CnasClient({ baseUrl: cnasBaseUrl });
    const bxpExpressClient = new BXPExpressClient({
      baseUrl: bxpExpressBaseUrl,
    });
    const hamletClient = new HamletClient({ baseUrl: hamsBaseUrl });
    const identityClient = new IdentityClient({
      baseUrlStargate: Environment.getAPIPrivateURL(),
      baseUrlIdentity: Environment.getIdentityBaseUrl(),
    });
    const stargateClient = new StargateClient({
      baseUrl: Environment.getAPIPrivateURL(),
    });
    return {
      bxpExpressClient,
      cofsClient,
      cnasClient,
      hamletClient,
      identityClient,
      stargateClient,
    };
  }

  renderComponent({ type, props, domRootId, defaultTrackProperties }) {
    const ufoSegmentName = domRootId === "signin-prompt-switcher" ? "signin-prompt" : "magnolia";
    const TagName = availableComponents[type] || null;
    if (!TagName) return;
    const propsWithDefaultTracking = { defaultTrackProperties, ...props };
    const {
      bxpExpressClient,
      cofsClient,
      cnasClient,
      hamletClient,
      identityClient,
      stargateClient,
    } = this.getClients();

    ReactDOM.render(
      <UFOSegment name={ufoSegmentName}>
      <Suspense fallback={<div></div>}>
        <SurfaceProvider>
          <FeatureFlagsProvider featureFlags={window.featureFlags}>
            <LocaleProvider locale={document.documentElement.lang}>
              <IntlNextProvider locale={document.documentElement.lang}>
                <IntlProvider
                  locale={document.documentElement.lang}
                  messages={props.labels}
                >
                  <CofsClientProvider client={cofsClient}>
                    <CnasClientProvider client={cnasClient}>
                      <BXPExpressClientProvider client={bxpExpressClient}>
                        <HamletClientProvider client={hamletClient}>
                          <IdentityClientProvider client={identityClient}>
                            <StargateClientProvider client={stargateClient}>
                              <FetchPricingProvider fetchPricing={fetchPricing}>
                                <TagName {...propsWithDefaultTracking} />
                              </FetchPricingProvider>
                            </StargateClientProvider>
                          </IdentityClientProvider>
                        </HamletClientProvider>
                      </BXPExpressClientProvider>
                    </CnasClientProvider>
                  </CofsClientProvider>
                </IntlProvider>
              </IntlNextProvider>
            </LocaleProvider>
          </FeatureFlagsProvider>
        </SurfaceProvider>
      </Suspense>
      </UFOSegment>,
      document.getElementById(domRootId)
    );
  }
}

let localePromise;
switch (document.documentElement.lang) {
  case "en":
    localePromise = import("react-intl/locale-data/en");
    break;
  case "ja":
    localePromise = import("react-intl/locale-data/ja");
    break;
  case "fr":
    localePromise = import("react-intl/locale-data/fr");
    break;
  case "de":
    localePromise = import("react-intl/locale-data/de");
    break;
  case "es":
    localePromise = import("react-intl/locale-data/es");
    break;
  case "br":
    localePromise = import("react-intl/locale-data/br");
    break;
  case "zh":
    localePromise = import("react-intl/locale-data/zh");
    break;
  case "ko":
    localePromise = import("react-intl/locale-data/ko");
    break;
  case "ru":
    localePromise = import("react-intl/locale-data/ru");
    break;
  case "pl":
    localePromise = import("react-intl/locale-data/pl");
    break;
  case "it":
    localePromise = import("react-intl/locale-data/it");
    break;
  case "nl":
    localePromise = import("react-intl/locale-data/nl");
    break;
  case "fi":
    localePromise = import("react-intl/locale-data/fi");
    break;
  case "hu":
    localePromise = import("react-intl/locale-data/hu");
    break;
  case "ro":
    localePromise = import("react-intl/locale-data/ro");
    break;
  case "cs":
    localePromise = import("react-intl/locale-data/cs");
    break;
  default:
    localePromise = import("react-intl/locale-data/en");
}

localePromise?.then(({ default: locale }) => {
  addLocaleData(locale);
  const app = new App();
  app.init();
});
